// ============================================================
// Summit League v2 — Recent Matches Component
// Last 5 completed matches with win/loss colors
// ============================================================

import React from 'react';
import type { RecentMatch } from '../../types/dashboard.types';
import {
  formatMatchType,
  getRelativeTime,
  DefaultTeamIconSmall,
} from './DashboardHelpers';

// ── Props ──────────────────────────────────────────────────

interface RecentMatchesProps {
  matches: RecentMatch[];
  loading: boolean;
  onMatchClick: (matchId: number) => void;
}

// ── Component ──────────────────────────────────────────────

const RecentMatches: React.FC<RecentMatchesProps> = ({
  matches,
  loading,
  onMatchClick,
}) => {
  return (
    <section className="sl-card recent-matches-card">
      <div className="recent-matches-header">
        <h2 className="recent-matches-title">Recent Matches</h2>
      </div>

      {loading ? (
        <div className="recent-matches-list-v2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="sl-skeleton-row sl-skeleton-pulse"
              style={{ height: 56 }}
            />
          ))}
        </div>
      ) : matches.length === 0 ? (
        <div className="sl-available-matches-empty">
          <div className="sl-available-matches-empty-title">No recent matches</div>
          <div className="sl-available-matches-empty-text">
            Completed matches will appear here.
          </div>
        </div>
      ) : (
        <div className="recent-matches-list-v2">
          {matches.map((match) => {
            const isTeamAWinner = match.winnerTeamId === match.teamA.teamId;
            const isTeamBWinner = match.winnerTeamId === match.teamB.teamId;

            return (
              <button
                key={match.matchId}
                className="recent-match-row-v2"
                onClick={() => onMatchClick(match.matchId)}
              >
                {/* Team A (left) */}
                <div className="recent-match-side">
                  <div className="recent-match-mark">
                    {match.teamA.logoUrl ? (
                      <img src={match.teamA.logoUrl} alt="" />
                    ) : (
                      <DefaultTeamIconSmall />
                    )}
                  </div>
                  <span
                    className={`recent-match-team ${
                      isTeamAWinner
                        ? 'recent-team--win'
                        : isTeamBWinner
                        ? 'recent-team--loss'
                        : ''
                    }`}
                  >
                    {match.teamA.teamName}
                  </span>
                </div>

                {/* Center: Score */}
                <div className="recent-match-center">
                  <span className="recent-match-score">
                    {match.teamAScore} - {match.teamBScore}
                  </span>
                  <span className="recent-match-subtitle">
                    {formatMatchType(match.matchType)} &middot;{' '}
                    {getRelativeTime(match.completedAt)}
                  </span>
                </div>

                {/* Team B (right) */}
                <div className="recent-match-side recent-match-side--right">
                  <span
                    className={`recent-match-team recent-match-team--right ${
                      isTeamBWinner
                        ? 'recent-team--win'
                        : isTeamAWinner
                        ? 'recent-team--loss'
                        : ''
                    }`}
                  >
                    {match.teamB.teamName}
                  </span>
                  <div className="recent-match-mark">
                    {match.teamB.logoUrl ? (
                      <img src={match.teamB.logoUrl} alt="" />
                    ) : (
                      <DefaultTeamIconSmall />
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default RecentMatches;